import { Injectable } from '@angular/core';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { CordovaPlugin } from '@omni/services/cordova';
import { DeviceService } from '@omni/services/device/device.service';
import { readSafeAreaInsets } from '@omni/utility/safe-area-insets';

/**
 * Sets the color of the system bar icons in the native iOS and Android apps. The app draws edge to edge with
 * dark icons over its light screens; a full-screen dark surface switches to light icons while it covers the
 * bars and restores the dark icons when it leaves. When the page reports no top inset, the WebView sits below
 * the status bar, the bar shows the window background, and its icons keep the theme's color.
 */
@Injectable({ providedIn: 'root' })
export class StatusBarAppearanceService {
  readonly #cordovaHelpers = new CordovaPlugin('CordovaHelpers');

  constructor(
    private readonly statusBar: StatusBar,
    private readonly device: DeviceService,
  ) {}

  /**
   * Shows light status bar icons, and on Android light navigation bar icons, over a dark surface, or the
   * app's dark icons otherwise. The web and desktop apps have no system bars and are unaffected, and neither
   * is a status bar the page does not draw under.
   *
   * @param light Whether the icons should be light.
   */
  setLightIcons(light: boolean): void {
    if (
      !this.device.isNativeApp ||
      this.device.deviceFlags.electron ||
      readSafeAreaInsets().top === 0
    ) {
      return;
    }
    if (this.device.deviceFlags.android) {
      this.#cordovaHelpers
        .exec('setLightSystemBarIcons', light)
        .catch(() => undefined);
    } else if (this.device.deviceFlags.ios) {
      if (light) {
        this.statusBar.styleLightContent();
      } else {
        this.statusBar.styleDefault();
      }
    }
  }
}
